import { Outlet, Link, useLocation } from 'react-router';
import { useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useAppStore } from '../../stores/appStore';
import {
  User,
  HelpCircle,
  Menu,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Zap,
  Link2,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// Wappler sidebar: Home → Services → Apps → Usage → Subscription
// Additional Connectfy items: Smart Correct, M-Files, Profile, Contact, Docs
const navItems = [
  { path: '/', label: 'Home', svg: 'home' },
  { path: '/services', label: 'Services', svg: 'services' },
  { path: '/apps', label: 'Apps', svg: 'apps' },
  { path: '/usage', label: 'Usage', svg: 'usage' },
  { path: '/subscription', label: 'Subscription', svg: 'subscription' },
  // Separator
  { separator: true },
  { path: '/smart-correct', label: 'Smart Correct', icon: Zap },
  { path: '/connections/mfiles', label: 'M-Files', icon: Link2 },
  // Separator
  { separator: true },
  { path: '/profile', label: 'Profile', icon: User },
  { path: '/help/contact', label: 'Contact', icon: HelpCircle },
  { path: '/docs/apps', label: 'Docs', icon: BookOpen },
];

// Wappler SVG icons for sidebar items (matches original portal)
function WapplerIcon({ name, active }: { name: string; active: boolean }) {
  const fill = active ? '#cc0404' : '#A39C98';
  switch (name) {
    case 'home':
      return (
        <svg width="20" height="20" viewBox="0 0 30 30" fill={fill} xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" clipRule="evenodd" d="M15.0005 0.710938L29.4882 12.0752L27.5127 14.333L15.0005 4.69724L2.48821 14.333L0.512695 12.0752L15.0005 0.710938Z" />
          <path d="M15.0005 0.710938L27.1076 10.2079V26.4472H18.0005V18.9473H12.0005V26.4472H3.00045V10.1238L15.0005 0.710938Z" />
        </svg>
      );
    case 'services':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 30 30" fill={fill}>
          <path d="M15 21C15 20.1716 15.6716 19.5 16.5 19.5H27C27.8284 19.5 28.5 20.1716 28.5 21V27C28.5 27.8284 27.8284 28.5 27 28.5H16.5C15.6716 28.5 15 27.8284 15 27V21Z" />
          <path d="M15 3C15 2.17157 15.6716 1.5 16.5 1.5H27C27.8284 1.5 28.5 2.17157 28.5 3V15C28.5 15.8284 27.8284 16.5 27 16.5H16.5C15.6716 16.5 15 15.8284 15 15V3Z" />
          <path d="M1.5 15C1.5 14.1716 2.17157 13.5 3 13.5H10.5C11.3284 13.5 12 14.1716 12 15V27C12 27.8284 11.3284 28.5 10.5 28.5H3C2.17157 28.5 1.5 27.8284 1.5 27V15Z" />
          <path d="M1.5 3C1.5 2.17157 2.17157 1.5 3 1.5H10.5C11.3284 1.5 12 2.17157 12 3V9C12 9.82843 11.3284 10.5 10.5 10.5H3C2.17157 10.5 1.5 9.82843 1.5 9V3Z" />
        </svg>
      );
    case 'apps':
      return (
        <svg width="20" height="20" viewBox="0 0 30 30" fill={fill} xmlns="http://www.w3.org/2000/svg">
          <path d="M5.83898 8.59999H1.26271C0.805085 8.59999 0.5 8.86999 0.5 9.27499V26.825C0.5 27.23 0.805085 27.5 1.26271 27.5H5.83898C6.29661 27.5 6.60169 27.23 6.60169 26.825V9.27499C6.60169 8.86999 6.29661 8.59999 5.83898 8.59999Z" />
          <path d="M22.7712 4.55013C22.6186 4.14513 22.3135 4.0101 21.8559 4.0101L17.4322 4.82007C16.9745 4.95507 16.822 5.2252 16.822 5.6302L21.5508 26.8251C21.7034 27.2301 22.0084 27.3652 22.4661 27.3652L26.8898 26.5552C27.3474 26.4202 27.5 26.1501 27.5 25.7451L22.7712 4.55013Z" />
          <path d="M14.9915 0.5H10.4152C9.95761 0.5 9.65253 0.77 9.65253 1.175V26.825C9.65253 27.23 9.95761 27.5 10.4152 27.5H14.9915C15.4491 27.5 15.7542 27.23 15.7542 26.825V1.175C15.7542 0.77 15.4491 0.5 14.9915 0.5Z" />
        </svg>
      );
    case 'usage':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 474.565 474.565" fill={fill}>
          <g>
            <g>
              <path d="M255.204,102.3c-0.606-11.321-12.176-9.395-23.465-9.395C240.078,95.126,247.967,98.216,255.204,102.3z" />
              <path d="M134.524,73.928c-43.825,0-63.997,55.471-28.963,83.37c11.943-31.89,35.718-54.788,66.886-63.826C163.921,81.685,150.146,73.928,134.524,73.928z" />
              <path d="M43.987,148.617c1.786,5.731,4.1,11.229,6.849,16.438L36.44,179.459c-3.866,3.866-3.866,10.141,0,14.015l25.375,25.383c1.848,1.848,4.38,2.888,7.019,2.888c2.61,0,5.125-1.04,7.005-2.888l14.38-14.404c2.158,1.142,4.55,1.842,6.785,2.827c0-0.164-0.016-0.334-0.016-0.498c0-11.771,1.352-22.875,3.759-33.302c-17.362-11.174-28.947-30.57-28.947-52.715c0-34.592,28.139-62.739,62.723-62.739c23.418,0,43.637,13.037,54.43,32.084c11.523-1.429,22.347-1.429,35.376,1.033c-1.676-5.07-3.648-10.032-6.118-14.683l14.396-14.411c1.878-1.856,2.918-4.38,2.918-7.004c0-2.625-1.04-5.148-2.918-7.004l-25.361-25.367c-1.94-1.941-4.472-2.904-7.003-2.904c-2.532,0-5.063,0.963-6.989,2.904l-14.442,14.411c-5.217-2.764-10.699-5.078-16.444-6.825V9.9c0-5.466-4.411-9.9-9.893-9.9h-35.888c-5.451,0-9.909,4.434-9.909,9.9v20.359c-5.73,1.747-11.213,4.061-16.446,6.825L75.839,22.689c-1.942-1.941-4.473-2.904-7.005-2.904c-2.531,0-5.077,0.963-7.003,2.896L36.44,48.048c-1.848,1.864-2.888,4.379-2.888,7.012c0,2.632,1.04,5.148,2.888,7.004l14.396,14.403c-2.75,5.218-5.063,10.708-6.817,16.438H23.675c-5.482,0-9.909,4.441-9.909,9.915v35.889c0,5.458,4.427,9.908,9.909,9.908H43.987z" />
              <path d="M354.871,340.654c15.872-8.705,26.773-25.367,26.773-44.703c0-28.217-22.967-51.168-51.184-51.168c-9.923,0-19.118,2.966-26.975,7.873c-4.705,18.728-12.113,36.642-21.803,52.202C309.152,310.022,334.357,322.531,354.871,340.654z" />
              <path d="M460.782,276.588c0-5.909-4.799-10.693-10.685-10.693H428.14c-1.896-6.189-4.411-12.121-7.393-17.75l15.544-15.544c2.02-2.004,3.137-4.721,3.137-7.555c0-2.835-1.118-5.553-3.137-7.563l-27.363-27.371c-2.08-2.09-4.829-3.138-7.561-3.138c-2.734,0-5.467,1.048-7.547,3.138l-15.576,15.552c-5.623-2.982-11.539-5.481-17.751-7.369v-21.958c0-5.901-4.768-10.685-10.669-10.685H311.11c-2.594,0-4.877,1.04-6.739,2.578c3.26,11.895,5.046,24.793,5.046,38.552c0,8.735-0.682,17.604-1.956,26.423c7.205-2.656,14.876-4.324,22.999-4.324c36.99,0,67.086,30.089,67.086,67.07c0,23.637-12.345,44.353-30.872,56.303c13.48,14.784,24.195,32.324,31.168,51.976c1.148,0.396,2.344,0.684,3.54,0.684c2.733,0,5.467-1.04,7.563-3.13l27.379-27.371c2.004-2.004,3.106-4.721,3.106-7.555s-1.102-5.551-3.106-7.563l-15.576-15.552c2.982-5.621,5.497-11.555,7.393-17.75h21.957c2.826,0,5.575-1.118,7.563-3.138c2.004-1.996,3.138-4.72,3.138-7.555L460.782,276.588z" />
              <path d="M376.038,413.906c-16.602-48.848-60.471-82.445-111.113-87.018c-16.958,17.958-37.954,29.351-61.731,29.351c-23.759,0-44.771-11.392-61.713-29.351c-50.672,4.573-94.543,38.17-111.145,87.026l-9.177,27.013c-2.625,7.773-1.368,16.338,3.416,23.007c4.783,6.671,12.486,10.631,20.685,10.631h315.853c8.215,0,15.918-3.96,20.702-10.631c4.767-6.669,6.041-15.234,3.4-23.007L376.038,413.906z" />
              <path d="M120.842,206.782c0,60.589,36.883,125.603,82.352,125.603c45.487,0,82.368-65.014,82.368-125.603C285.563,81.188,120.842,80.939,120.842,206.782z" />
            </g>
          </g>
        </svg>
      );
    case 'subscription':
      return (
        <svg width="20" height="20" viewBox="0 0 30 30" fill={fill} xmlns="http://www.w3.org/2000/svg">
          <path d="M28.5 28.748H22.5V0.248047L28.5 6.24805V28.748Z" />
          <path d="M0 28.748H4.5V18.6978L0 20.6479V28.748Z" />
          <path fillRule="evenodd" clipRule="evenodd" d="M6 28.748V6.24805L21 0.248047V28.748H6ZM9.75 23.798L12.75 22.748V19.748L9.75 20.798V23.798ZM9.75 17.798L12.75 16.748V13.748L9.75 14.798V17.798ZM9.75 11.798L12.75 10.748V7.74805L9.75 8.79797V11.798ZM14.25 22.298L17.25 21.248V18.248L14.25 19.298V22.298ZM14.25 16.298L17.25 15.248V12.248L14.25 13.298V16.298ZM14.25 10.298L17.25 9.24805V6.24805L14.25 7.29797V10.298Z" />
        </svg>
      );
    default:
      return null;
  }
}

export function DashboardLayout() {
  const { user } = useAuth();
  const { sidebarOpen, toggleSidebar } = useAppStore();
  const location = useLocation();

  // Auto-collapse mobile sidebar on navigation
  useEffect(() => {
    if (sidebarOpen && window.innerWidth < 1024) {
      toggleSidebar();
    }
  }, [location.pathname]);

  // Intentional: startsWith allows /subscription to highlight for /subscription/request etc.
  function isActive(path: string) {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  }

  return (
    <div className="min-h-screen bg-[#FAFAFB]">
      {/* Top navbar — matches Wappler: white bg, inset shadow, logo 200px */}
      <header className="bg-white fixed top-0 left-0 right-0 z-50 h-16" style={{ boxShadow: 'inset 0px -1px 0px #E2E2EA', borderBottom: '1px solid rgba(226,226,234,0.5)' }}>
        <div className="flex justify-between h-16 items-center px-5">
          <div className="flex items-center gap-3">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-[4px] hover:bg-gray-100 lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/">
              <img src="/assets/logo.png" alt="Connectfy" className="w-[200px]" />
            </Link>
          </div>
          <div className="flex items-center gap-4">
            {/* Notification bell — matches Wappler */}
            <Link to="/profile/notifications" className="relative me-1">
              <i className="fas fa-bell fa-lg text-[#767676]"></i>
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:bg-gray-50 rounded-[4px] px-2 py-1">
                  <span className="text-sm text-[#767676] hidden sm:inline" style={{ fontFamily: '"Lato", "Source Sans Pro", sans-serif' }}>
                    {user?.firstname} {user?.lastname}
                  </span>
                  <Avatar className="h-8 w-8">
                    <AvatarImage
                      src={user?.avatar ? `/uploads/${user.avatar}` : undefined}
                      alt={`${user?.firstname} ${user?.lastname}`}
                    />
                    <AvatarFallback className="text-xs">
                      {user?.firstname?.[0]}{user?.lastname?.[0]}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="cursor-pointer">
                    <i className="fas fa-user mr-2"></i>My Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/profile/security" className="cursor-pointer">
                    <i className="fas fa-cog mr-2"></i>Change Password
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/logout" className="cursor-pointer text-[#cc0404]">
                    <i className="fas fa-sign-out-alt mr-2"></i>Log Out
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Sidebar — matches Wappler: collapsible, SVG icons, 85px collapsed */}
      <aside
        className={cn(
          'fixed top-16 left-0 bottom-0 bg-white border-r z-40 overflow-y-auto transition-all duration-200',
          sidebarOpen ? 'w-64' : 'w-[85px]'
        )}
        style={{ borderRight: '1px solid rgba(226,226,234,0.5)' }}
      >
        {/* Collapse toggle — desktop only */}
        <div className="hidden lg:flex justify-end p-2">
          <button
            onClick={toggleSidebar}
            className="p-1.5 rounded-[4px] hover:bg-gray-100 text-[#767676]"
          >
            {sidebarOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </div>
        <nav className={cn('px-2 space-y-1', !sidebarOpen && 'px-1')}>
          {navItems.map((item, idx) => {
            // Separator
            if ('separator' in item && item.separator) {
              return (
                <div key={`sep-${idx}`} className="my-2 mx-3 border-t border-gray-200" />
              );
            }

            const active = isActive(item.path!);

            // Items with Wappler SVG icons
            if ('svg' in item && item.svg) {
              return (
                <Link
                  key={item.path!}
                  to={item.path!}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-[4px] text-sm relative',
                    !sidebarOpen && 'justify-center px-0',
                    active
                      ? 'text-[#cc0404] font-medium'
                      : 'text-[#A39C98] hover:bg-gray-50'
                  )}
                  title={!sidebarOpen ? item.label : undefined}
                >
                  <span className="flex-shrink-0">
                    <WapplerIcon name={item.svg as string} active={active} />
                  </span>
                  {sidebarOpen && <span>{item.label}</span>}
                </Link>
              );
            }

            // Items with Lucide icons (Smart Correct, M-Files, Profile, Contact, Docs)
            const Icon = (item as any).icon;
            return (
              <Link
                key={item.path!}
                to={item.path!}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-[4px] text-sm relative',
                  !sidebarOpen && 'justify-center px-0',
                  active
                    ? 'text-[#cc0404] font-medium'
                    : 'text-[#A39C98] hover:bg-gray-50'
                )}
                title={!sidebarOpen ? item.label : undefined}
              >
                <Icon className={cn('h-5 w-5 flex-shrink-0', active ? 'text-[#cc0404]' : 'text-[#A39C98]')} />
                {sidebarOpen && item.label}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Main content */}
      <main
        className={cn(
          'pt-16 transition-all duration-200',
          sidebarOpen ? 'lg:ml-64' : 'lg:ml-[85px]'
        )}
      >
        <div className="p-6 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}